/**
 * Canvas hit-region registry (WI #5531).
 *
 * Painters that draw interactive chrome record their clickable regions here as
 * they draw; the interaction layer queries it afterwards. This decouples drawing
 * from hit-testing: the BannerPainter is the producer, `hitTestBanner` is the
 * consumer, and neither needs to know about the other.
 */
import { inRect } from '../draw/shapes';
import type { BannerHit, BannerRegion, Rect } from './types';

const PHASE_STATE_HIT = 12;
const MILESTONE_STATE_HIT = 9;
/** WI #5622: half-size of the padlock chip's square hit box — the chip's own
 * radius, so the box (y 101–117 at chip y 109) stays clear of the phase
 * ribbon band (y ≤ 100), which onPointerUp resolves independently. */
const COMPRESS_FIX_HIT = 8;

/** Clickable glyph centres from the last draw; hit within a square or circle around each. */
class CentreHits {
  private centres: { id: string; x: number; y: number }[] = [];

  constructor(
    private readonly reach: number,
    private readonly shape: 'square' | 'circle' = 'square',
  ) {}

  reset(): void {
    this.centres = [];
  }

  add(id: string, x: number, y: number): void {
    this.centres.push({ id, x, y });
  }

  hit(x: number, y: number): string | null {
    for (const c of this.centres) {
      const dx = Math.abs(x - c.x);
      const dy = Math.abs(y - c.y);
      const inside =
        this.shape === 'circle'
          ? dx * dx + dy * dy <= this.reach * this.reach
          : dx <= this.reach && dy <= this.reach;
      if (inside) {
        return c.id;
      }
    }

    return null;
  }
}

export class HitRegistry {
  /** Clickable banner regions from the last content draw (screen space). */
  private banners: BannerRegion[] = [];
  /** Phase state icon centres from the last bg draw (screen space). */
  private phaseStateIcons = new CentreHits(PHASE_STATE_HIT);
  /** WI #5094: milestone state badge centres from the last content draw. */
  private milestoneStateIcons = new CentreHits(MILESTONE_STATE_HIT, 'circle');
  /** Drawn phase title text bounds from the last bg draw (screen space).
   * WI #5550: the pointer affordance covers the title text only, so the
   * painter records what it actually drew (truncation included). */
  private phaseTitles: { phaseId: string; rect: Rect }[] = [];
  /** Drawn workstream title text bounds from the last overlay draw. */
  private workstreamTitles: { workstreamId: string; rect: Rect }[] = [];
  /** WI #5622: compress-tool fix toggle (padlock chip) centres from the last
   * content draw (screen space); recorded only while the compress tool is armed. */
  private compressFixToggles = new CentreHits(COMPRESS_FIX_HIT);

  resetBanners(): void {
    this.banners = [];
  }

  resetPhaseStateIcons(): void {
    this.phaseStateIcons.reset();
  }

  resetMilestoneStateIcons(): void {
    this.milestoneStateIcons.reset();
  }

  resetPhaseTitles(): void {
    this.phaseTitles = [];
  }

  resetWorkstreamTitles(): void {
    this.workstreamTitles = [];
  }

  resetCompressFixToggles(): void {
    this.compressFixToggles.reset();
  }

  addPhaseTitle(phaseId: string, rect: Rect): void {
    this.phaseTitles.push({ phaseId, rect });
  }

  addWorkstreamTitle(workstreamId: string, rect: Rect): void {
    this.workstreamTitles.push({ workstreamId, rect });
  }

  hitTestPhaseTitle(x: number, y: number): string | null {
    for (const title of this.phaseTitles) {
      if (inRect(x, y, title.rect)) {
        return title.phaseId;
      }
    }

    return null;
  }

  hitTestWorkstreamTitle(x: number, y: number): string | null {
    for (const title of this.workstreamTitles) {
      if (inRect(x, y, title.rect)) {
        return title.workstreamId;
      }
    }

    return null;
  }

  addPhaseStateIcon(phaseId: string, x: number, y: number): void {
    this.phaseStateIcons.add(phaseId, x, y);
  }

  hitTestPhaseStateIcon(x: number, y: number): string | null {
    return this.phaseStateIcons.hit(x, y);
  }

  addMilestoneStateIcon(milestoneId: string, x: number, y: number): void {
    this.milestoneStateIcons.add(milestoneId, x, y);
  }

  hitTestMilestoneStateIcon(x: number, y: number): string | null {
    return this.milestoneStateIcons.hit(x, y);
  }

  /** WI #5622: record a milestone's compress fix toggle centre for this frame. */
  addCompressFixToggle(milestoneId: string, x: number, y: number): void {
    this.compressFixToggles.add(milestoneId, x, y);
  }

  /** WI #5622: resolve the milestone whose fix toggle is under a screen point. */
  hitTestCompressFixToggle(x: number, y: number): string | null {
    return this.compressFixToggles.hit(x, y);
  }

  /** Record one banner's clickable sub-regions for this frame. */
  addBanner(region: BannerRegion): void {
    this.banners.push(region);
  }

  /**
   * Resolve a banner sub-region under a screen point (topmost banner wins): the
   * state glyph, the deadline text, or the card body. Returns null off banners,
   * so the caller can fall through to scene hit-testing.
   */
  hitTestBanner(x: number, y: number): BannerHit | null {
    for (let i = this.banners.length - 1; i >= 0; i--) {
      const banner = this.banners[i];
      if (banner.state && inRect(x, y, banner.state)) {
        return { activityId: banner.activityId, region: 'state' };
      }
      if (banner.deadline && inRect(x, y, banner.deadline)) {
        return { activityId: banner.activityId, region: 'deadline' };
      }
      if (inRect(x, y, banner.panel)) {
        return { activityId: banner.activityId, region: 'body' };
      }
    }

    return null;
  }
}
